import './style.scss';

// Re-verified from a native 30fps, frame-by-frame extraction of
// t=169.0-171.8s. Each character was tracked individually (connected-
// component blob tracking, matched frame-to-frame by nearest centroid,
// traced backward from a settled reference frame to each character's own
// FIRST-VISIBLE frame). That first-visible position is not where the
// entrance actually starts, though - the character is already fading in
// (autoAlpha) by the time it crosses the visibility threshold, so it's
// partway along its real path, not standing still off-camera.
//
// So each character's real start is reconstructed geometrically: draw the
// line from its final position through its first-visible position, and
// extend that same line out to the edge of the stage (+60px past it, so
// it's genuinely starting outside the component, not flush on the
// boundary). That's the "comes from outside the block" starting point per
// the client brief - not the small first-visible offset by itself.
//
// OFFSETS below are (edge-start - final) in % of the stage's own
// width/height, in ACF row order (1 Rana ... 13 Marwa, matching
// render.php's $figma_slots comment). Character 3 (Shahd)'s first-visible
// track locked onto a mouse-cursor artifact (confirmed by direct frame
// inspection) and was interpolated from its layout neighbors before this
// extrapolation - flagged as the one lower-confidence entry.
//
// Rotation could not be reliably extracted per character: automated
// orientation estimates (image-moment principal axis) don't correspond
// to intuitive rotation delta for these irregular shapes, and hand-timing
// 13 rotations by eye across dozens of frames each wasn't reliable either.
// Kept as a bounded random offset, same as before - this is the one part
// of this entrance that's an approximation, not a measurement.
const CHARACTER_START_OFFSET_PCT = [
	{x: 1.8, y: 74.3},    // 1 Rana
	{x: 38.7, y: 30.0},   // 2 Rawan
	{x: 19.8, y: 20.7},   // 3 Shahd - low confidence, see comment above
	{x: -2.7, y: 49.3},   // 4 Khaled
	{x: 17.0, y: -7.1},   // 5 Khalil
	{x: 29.5, y: -40.6},  // 6 Hossam
	{x: 0.5, y: 12.9},    // 7 Kareem
	{x: -2.5, y: 15.9},   // 8 Walid
	{x: -3.6, y: 21.5},   // 9 Zeyad
	{x: -18.2, y: -1.5},  // 10 Mido
	{x: 5.7, y: 49.1},    // 11 Omar
	{x: -23.3, y: 28.0},  // 12 Nada
	{x: -40.9, y: 6.8},   // 13 Marwa
];

function initBridgeContact(block) {
	// ACF can disable the decorative cast entirely. In that state render.php
	// emits no character markup/images, and JS exits before touching GSAP or
	// ScrollTrigger so there is no character animation work on the frontend.
	if (block.dataset.charactersEnabled !== 'true') {
		return;
	}

	const {gsap, ScrollTrigger} = window.rebredgVendors || {};

	if (!gsap || !ScrollTrigger) {
		return;
	}

	if (block.dataset.bridgeContactInitialized === 'true') {
		return;
	}

	block.dataset.bridgeContactInitialized = 'true';

	if (block.closest('.editor-styles-wrapper')) {
		return;
	}

	const stage = block.querySelector('.bridge-contact-stage');
	const characters = Array.from(block.querySelectorAll('.bridge-contact-character'));

	if (!stage || !characters.length) {
		return;
	}

	if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
		return;
	}

	gsap.registerPlugin(ScrollTrigger);

	// Order top-to-bottom by each character's own final Y (matches the
	// observed wave: the frames show characters appearing in bands from the
	// top of the arrangement downward, not randomly or leader-first). This
	// only controls stagger timing - the x/y offset below is looked up by
	// each element's original ACF-row index, independent of this order.
	const byFinalY = [...characters].sort((a, b) => {
		return a.getBoundingClientRect().top - b.getBoundingClientRect().top;
	});

	gsap.timeline({
		scrollTrigger: {
			trigger: stage,
			start: 'top center',
			end: 'bottom 30%',
			// play/reverse on every crossing, from either direction - not
			// just the two boundary-specific ones (was 'play none reverse
			// none', which left it out of sync scrolling past the bottom)
			toggleActions: 'play reverse play reverse'
		}
	}).from(byFinalY, {
		x: (i, target) => {
			const idx = characters.indexOf(target);
			const offset = CHARACTER_START_OFFSET_PCT[idx];
			return offset ? stage.getBoundingClientRect().width * (offset.x / 100) : 0;
		},
		y: (i, target) => {
			const idx = characters.indexOf(target);
			const offset = CHARACTER_START_OFFSET_PCT[idx];
			return offset ? stage.getBoundingClientRect().height * (offset.y / 100) : 0;
		},
		scale: 0.5,
		autoAlpha: 0,
		rotation: () => `+=${gsap.utils.random(-35, 35)}`,
		// duration up from .6 -> .8: the travel distance grew a lot (edge of
		// component, not the small first-visible offset), .6 read too fast
		// for how far these now have to move
		duration: .8,
		ease: 'power3.out',
		stagger: {
			amount: .35,
			from: 'start'
		}
	});

	ScrollTrigger.refresh();
}

export function bridge_contact(root = document) {
	const selector = '[data-component="bridge-contact"]';
	const context = root || document;
	const blocks = [];

	if (context.nodeType === 1 && context.matches(selector)) {
		blocks.push(context);
	}

	if (context.querySelectorAll) {
		blocks.push(...context.querySelectorAll(selector));
	}

	blocks.forEach(initBridgeContact);
}
